import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  CalendarCheck, Hourglass, Flag, X, Info, Check, Clock,
} from 'lucide-react';
import IconButton from '../../components/ui/IconButton';
import Avatar from '../../components/ui/Avatar';
import isInsidePortalLayer from '../../utils/isInsidePortalLayer';
import { holidayBlock, approvalRows, notDeductedCounts } from './calendarModel';
import {
  fmtDayMonth, fmtLongDate, fmtRange, fmtRangeWithYear, weekdayLong, weekdaySpan, fmtDays, fmtHours, isoToDayKey,
} from './calendarFormat';
import { Tag } from './DayHoverTip';
import useLeaveFlowSteps from './useLeaveFlowSteps';

const NARROW_QUERY = '(max-width: 600px)';
const KNOWN_SOURCES = ['auto', 'bulk', 'excel_import', 'public_api'];

const isNarrow = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  ? !!window.matchMedia(NARROW_QUERY)?.matches
  : false);

const splitName = (name) => {
  const p = String(name || '').trim().split(/\s+/);
  return { firstName: p[0] || '', lastName: p.length > 1 ? p[p.length - 1] : '' };
};

/**
 * Ayrıntı kartı (spec §3.6) — tıklanan günün yanında açılan, sayfadan çıkarmayan açılır kart; dar ekranda
 * alttan tam genişlik panel. document.body'ye portal'lanır (data-portal-layer). Kapanış: dışarı mousedown,
 * ×, Esc, sayfa kaydırma (lejant değişimi sayfada kapatır). Açılınca odak karta, kapanınca güne döner.
 */
export default function DayDetailCard({
  day, anchor, index, holidayCalendars, showLeaves, onClose,
}) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [pos, setPos] = useState(null);
  const [narrow] = useState(isNarrow);

  const leaves = day.cell.leaves;
  const holiday = day.cell.holiday;
  const flowUserId = leaves.length ? leaves[0].userId : null;
  // Zincir iznin başladığı akıştan (sabitlenmişse — spec 2026-11-01 §4.4). Aynı güne düşen iki izin farklı akışlara
  // sabitlenmişse ilkinin akışı kullanılır (aynı gün iki izin istisnadır).
  const flowPin = leaves.length
    ? { pinned: !!leaves[0].approvalFlowPinned, flowId: leaves[0].approvalFlowId || null }
    : null;
  const { steps, loading: stepsLoading } = useLeaveFlowSteps(flowUserId, showLeaves && leaves.length > 0, flowPin);

  useLayoutEffect(() => {
    if (!ref.current || !anchor || narrow) return;
    const r = anchor.getBoundingClientRect();
    const pw = ref.current.offsetWidth;
    const ph = ref.current.offsetHeight;
    let left = r.right + 10;
    if (left + pw > window.innerWidth - 8) left = r.left - pw - 10;
    if (left < 8) left = Math.max(8, Math.min(r.left + r.width / 2 - pw / 2, window.innerWidth - pw - 8));
    let top = r.top + r.height / 2 - 40;
    top = Math.max(64, Math.min(top, window.innerHeight - ph - 8));
    setPos({ left, top });
  }, [anchor, narrow, day.date, steps]);

  // Odak: açılınca karta, kapanınca tıklanan güne.
  useEffect(() => {
    ref.current?.focus({ preventScroll: true });
    return () => {
      if (anchor && typeof anchor.focus === 'function' && document.contains(anchor)) anchor.focus({ preventScroll: true });
    };
  }, [anchor]);

  useEffect(() => {
    const onMouseDown = (e) => {
      if (ref.current?.contains(e.target)) return;
      if (e.target?.closest?.('.day.clickable')) return;   // başka güne tıklama kartı değiştirir
      if (isInsidePortalLayer(e.target)) return;
      onClose();
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const onScroll = (e) => {
      if (ref.current && e.target instanceof Node && ref.current.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [onClose]);

  const calendarName = (id) => holidayCalendars.find((c) => c.id === id)?.name || null;
  const closeButton = (
    <IconButton size="sm" onClick={onClose} aria-label={t('myCalendar.card.close')} data-testid="calendar-card-close">
      <X size={16} />
    </IconButton>
  );

  const title = leaves.length ? leaves[0].leaveVariationName : holiday?.name;

  return createPortal(
    <Card
      ref={ref}
      role="dialog"
      aria-modal="false"
      aria-label={title}
      tabIndex={-1}
      data-portal-layer="calendar-card"
      data-testid="calendar-card"
      $narrow={narrow}
      style={narrow ? undefined : (pos || { left: -9999, top: -9999 })}
      $show={narrow || !!pos}
    >
      {leaves.length > 0
        ? leaves.map((l, i) => (
          <LeaveSection
            key={l.id}
            leave={l}
            day={day}
            steps={steps}
            stepsLoading={stepsLoading}
            showHoliday={i === 0 ? holiday : null}
            closeButton={i === 0 ? closeButton : null}
            divided={i > 0}
          />
        ))
        : holiday && (
          <HolidaySection
            day={day}
            holiday={holiday}
            index={index}
            calendarName={calendarName(holiday.calendarId)}
            closeButton={closeButton}
          />
        )}
    </Card>,
    document.body,
  );
}

function LeaveSection({
  leave, day, steps, stepsLoading, showHoliday, closeButton, divided,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const rows = approvalRows(leave, steps);
  const { offDays, holidays } = notDeductedCounts(leave);
  const notDeductedParts = [
    offDays ? t('myCalendar.card.notDeductedOff', { count: offDays }) : null,
    holidays ? t('myCalendar.card.notDeductedHoliday', { count: holidays }) : null,
  ].filter(Boolean);
  const requestDay = isoToDayKey(leave.createDate);

  const when = leave.isHourly
    ? {
      w1: `${fmtLongDate(t, leave.startDate)}, ${weekdayLong(t, leave.startDate)}`,
      w2: `${leave.startTime} – ${leave.endTime} · ${fmtHours(t, lang, leave.cost)}`,
    }
    : {
      w1: fmtRangeWithYear(t, leave.startDate, leave.lastLeaveDate),
      w2: `${weekdaySpan(t, leave.startDate, leave.lastLeaveDate)} · ${fmtDays(t, lang, leave.cost)}`,
    };

  const singleCurrent = rows.length === 1 && rows[0].key === 'current';
  // Geçmişi olmayan izinde satırlar akıştan türetilir: akış yüklenirken hiçbir şey yazılmaz (yanlış "Onaylandı" yok).
  const awaitingSteps = stepsLoading && !(leave.history || []).length;

  return (
    <Section $divided={divided} data-testid="calendar-card-leave">
      <Head>
        <Pico $kind={leave.status}>
          {leave.status === 'approved' ? <CalendarCheck size={18} /> : <Hourglass size={18} />}
        </Pico>
        <div>
          <Title>{leave.leaveVariationName}</Title>
          <Tag $kind={leave.status} style={{ margin: '4px 0 0' }}>{t(`myCalendar.tag.${leave.status}`)}</Tag>
        </div>
        {closeButton && <CloseSlot>{closeButton}</CloseSlot>}
      </Head>
      <Body>
        <When>
          <W1>{when.w1}</W1>
          <W2>{when.w2}</W2>
        </When>
        {notDeductedParts.length > 0 && (
          <Hint>
            <Info size={14} />
            <span>{t('myCalendar.card.notDeducted', { parts: notDeductedParts.join(', ') })}</span>
          </Hint>
        )}
        {showHoliday && (
          <Also data-testid="calendar-card-holiday-line">
            <i />
            {`${fmtDayMonth(t, day.date)}: ${showHoliday.name}`}
          </Also>
        )}

        <Sec>
          <SecLabel>{t('myCalendar.card.approvalProcess')}</SecLabel>
          {!awaitingSteps && rows.length === 0 && <Plain>{t(`myCalendar.tag.${leave.status}`)}</Plain>}
          {!awaitingSteps && singleCurrent && <Plain>{t('myCalendar.card.waitingApprover', { name: rows[0].name })}</Plain>}
          {!awaitingSteps && !singleCurrent && rows.map((r) => <StepRow key={r.key} row={r} />)}
        </Sec>

        {(leave.userNotes || leave.managerNotes) && (
          <Sec>
            <SecLabel>{t('myCalendar.card.notes')}</SecLabel>
            {leave.userNotes && <Note><b>{t('myCalendar.card.myNote')}</b>{leave.userNotes}</Note>}
            {leave.managerNotes && <Note><b>{t('myCalendar.card.managerNote')}</b>{leave.managerNotes}</Note>}
          </Sec>
        )}
      </Body>
      {requestDay && <Foot>{t('myCalendar.card.requestDate', { date: fmtLongDate(t, requestDay) })}</Foot>}
    </Section>
  );
}

function StepRow({ row }) {
  const { t } = useTranslation();
  const sourceLabel = row.source && KNOWN_SOURCES.includes(row.source) ? t(`myCalendar.card.source.${row.source}`) : null;
  const name = row.name || sourceLabel || '—';
  const subtitle = row.name && sourceLabel ? sourceLabel : row.title;
  const dayKey = isoToDayKey(row.date);
  let right = t(`myCalendar.card.step.${row.state}`);
  if (dayKey && (row.state === 'approved' || row.state === 'rejected')) right = `${right} · ${fmtDayMonth(t, dayKey)}`;
  const { firstName, lastName } = splitName(name);

  return (
    <Step $state={row.state} data-testid="calendar-card-step">
      <AvatarWrap $dim={row.state === 'next' || row.state === 'skipped'}>
        <Avatar src={row.photo || undefined} firstName={firstName} lastName={lastName} size={28} />
        {row.state === 'approved' && <Badge $kind="ok"><Check size={9} strokeWidth={3.5} /></Badge>}
        {row.state === 'waiting' && <Badge $kind="now"><Clock size={9} strokeWidth={3.5} /></Badge>}
        {row.state === 'rejected' && <Badge $kind="no"><X size={9} strokeWidth={3.5} /></Badge>}
      </AvatarWrap>
      <div>
        <StepName>{name}</StepName>
        {subtitle && <StepSub>{subtitle}</StepSub>}
      </div>
      <StepState $now={row.state === 'waiting'}>{right}</StepState>
    </Step>
  );
}

function HolidaySection({
  day, holiday, index, calendarName, closeButton,
}) {
  const { t, i18n } = useTranslation();
  const block = holidayBlock(index, day.date);
  return (
    <Section data-testid="calendar-card-holiday">
      <Head>
        <Pico $kind="holiday"><Flag size={18} /></Pico>
        <div>
          <Title>{holiday.name}</Title>
          <Tag $kind="holiday" style={{ margin: '4px 0 0' }}>
            {t(holiday.isHalfDay ? 'myCalendar.tag.halfDay' : 'myCalendar.tag.holiday')}
          </Tag>
        </div>
        <CloseSlot>{closeButton}</CloseSlot>
      </Head>
      <Body>
        <When>
          <W1>{fmtLongDate(t, day.date)}</W1>
          <W2>
            {weekdayLong(t, day.date)}
            {' · '}
            {holiday.isHalfDay ? t('myCalendar.tag.halfDay') : fmtDays(t, i18n.language, 1)}
          </W2>
        </When>
        {block && (
          <Hint>
            <Info size={14} />
            <span>{t('myCalendar.card.holidayBlock', { range: fmtRange(t, block.start, block.end), count: block.days })}</span>
          </Hint>
        )}
      </Body>
      {calendarName && <Foot>{t('myCalendar.card.holidayCalendar', { name: calendarName })}</Foot>}
    </Section>
  );
}

const Card = styled.div`
  position: fixed;
  z-index: 1300;
  width: 320px;
  max-height: calc(100vh - 80px);
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.surface.card};
  border-radius: 16px;
  box-shadow: 0 18px 48px rgba(${({ theme }) => theme.colors.shadow.rgb}, 0.2), 0 0 0 1px rgba(${({ theme }) => theme.colors.shadow.rgb}, 0.06);
  color: ${({ theme }) => theme.colors.text.primary};
  outline: none;
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  transition: opacity 0.15s;

  ${({ $narrow }) => $narrow && css`
    left: 8px;
    right: 8px;
    bottom: 8px;
    width: auto;
    max-height: 75vh;
    border-radius: 18px;
  `}
`;

const Section = styled.div`
  ${({ $divided, theme }) => $divided && css`border-top: 6px solid ${theme.colors.bgColor};`}
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 16px 12px;
`;

const Pico = styled.span`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  ${({ $kind, theme }) => {
    const c = theme.colors.calendar;
    if ($kind === 'holiday') return `background: ${c.holidayBg}; color: ${c.holidayText};`;
    if ($kind === 'approved') return `background: ${c.approvedTagBg}; color: ${theme.colors.primary.main};`;
    return `background: ${c.waitingBg}; color: ${c.waitingText};`;
  }}
`;

const Title = styled.div`
  font-size: 15px;
  font-weight: 800;
  line-height: 1.25;
`;

const CloseSlot = styled.div`
  margin-left: auto;
  flex-shrink: 0;
`;

const Body = styled.div`
  padding: 0 16px 16px;
  font-size: 13px;
`;

const When = styled.div`
  background: ${({ theme }) => theme.colors.bgColor};
  border-radius: 12px;
  padding: 10px 12px;
`;

const W1 = styled.div`
  font-weight: 700;
  font-size: 14px;
`;

const W2 = styled.div`
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: 2px;
`;

const Hint = styled.div`
  display: flex;
  gap: 6px;
  align-items: flex-start;
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 12px;
  margin-top: 8px;

  svg { margin-top: 1px; color: ${({ theme }) => theme.colors.calendar.faint}; flex-shrink: 0; }
`;

const Also = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 10px;
  padding: 8px 10px;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.calendar.holidaySoftBg};
  color: ${({ theme }) => theme.colors.calendar.holidayText};
  font-size: 12px;
  font-weight: 600;

  i { width: 8px; height: 8px; border-radius: 50%; background: ${({ theme }) => theme.colors.calendar.holidayText}; flex-shrink: 0; }
`;

const Sec = styled.div`
  margin-top: 14px;
`;

const SecLabel = styled.div`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.calendar.faint};
  margin-bottom: 8px;
`;

const Plain = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 600;
`;

const Step = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;

  & + & { margin-top: 10px; }
  & + &::before {
    content: '';
    position: absolute;
    left: 13px;
    top: -10px;
    height: 10px;
    width: 2px;
    background: ${({ theme }) => theme.colors.surface.border};
  }
`;

const AvatarWrap = styled.span`
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  opacity: ${({ $dim }) => ($dim ? 0.4 : 1)};
`;

const Badge = styled.span`
  position: absolute;
  right: -3px;
  bottom: -3px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.surface.card};
  color: ${({ theme }) => theme.colors.text.light.very};
  background: ${({ $kind, theme }) => {
    if ($kind === 'ok') return theme.colors.calendar.stepDone;
    if ($kind === 'now') return theme.colors.calendar.waitingBorder;
    return theme.colors.calendar.holidayText;
  }};
`;

const StepName = styled.div`
  font-weight: 700;
`;

const StepSub = styled.div`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 12px;
`;

const StepState = styled.span`
  margin-left: auto;
  font-size: 12px;
  white-space: nowrap;
  color: ${({ $now, theme }) => ($now ? theme.colors.calendar.waitingText : theme.colors.text.muted)};
  font-weight: ${({ $now }) => ($now ? 700 : 400)};
`;

const Note = styled.div`
  background: ${({ theme }) => theme.colors.bgColor};
  border-radius: 10px;
  padding: 8px 10px;

  & + & { margin-top: 6px; }
  b {
    display: block;
    font-size: 11px;
    color: ${({ theme }) => theme.colors.text.muted};
    font-weight: 700;
    margin-bottom: 2px;
  }
`;

const Foot = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.surface.border};
  padding: 10px 16px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;
